import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import * as homepageService from '../homepage/service';
import TopTrendingEpisodes from '../../Components/TopTrending/TopTrendingEpisodes';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { fetchKidsLandingData } from './service';
import * as loginService from '../Accounts/Login/service';
import * as registerService from '../Accounts/Register/service';
import { getUser } from '../../Redux/UserSlice/UserSlice';
import { getAccessToken } from '../../Redux/AToken/ATokenSlice';
import { getUserDetails } from '../../Redux/UserDetailsSlice/UserDetailsSlice';
import { checkIfEmailExists } from '../Subscription/service';
import ParentalPasscodeModal from '../../Components/Modals/ParentalPasscodeModal';
import { GoogleLogin, useGoogleLogin } from '@react-oauth/google';
import { ReactComponent as GoogleIcon } from '../../assets/Icons/googleIcon.svg';
import { ReactComponent as AppleIcon } from '../../assets/Icons/appleIcon.svg';
import ApplePrivateLoginModal from '../../Components/Modals/LoginModal/ApplePrivateLoginModal';
import { jwtDecode } from 'jwt-decode';
import { useNavigate, useLocation } from 'react-router-dom';
import Translate from '../../Components/Multilanguage/Translate';

const KidsLanding = ({ isLogin = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [email, setEmail] = useState(location.state?.email || '');
  const [password, setPassword] = useState('');
  const [isExisting, setIsExisting] = useState(null);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [googleUser, setGoogleUser] = useState();
  const [loading, setLoading] = useState(true);
  const [topTrending, setTopTrending] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [pendingUserId, setPendingUserId] = useState(null);
  const [applePrivateData, setApplePrivateData] = useState();
  const [applePrivateLogin, setApplePrivateLogin] = useState(false);
  const [applePrivateEmail, setApplePrivateEmail] = useState();
  const [emailErrorKey, setEmailErrorKey] = useState('');
  const emailInputRef = useRef(null);
  const projectInfo = useSelector((state) => state?.projectInfo?.value);
  const accessToken = useSelector((state) => state?.accessToken?.value);

  const appInfo = {
    projectDetails: projectInfo,
    accessToken: accessToken,
  };

  useEffect(() => {
    if (!isLogin) {
      const fetchData = async () => {
        setLoading(true);
        try {
          const apiResp = await fetchKidsLandingData(appInfo);
          if (apiResp && apiResp.data && apiResp.data.data && Array.isArray(apiResp.data.data.shows)) {
            setTopTrending(apiResp.data.data);
          } else {
            setTopTrending(null);
          }
        } catch (err) {
          setTopTrending(null);
        }
        setLoading(false);
      };
      fetchData();
    }
  }, [projectInfo, isLogin]);

  const getEmailValidationKey = (value) => {
    const emailRegex = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;
    if (!value || !value.trim()) {
      return 'email_required_message';
    }
    if (!emailRegex.test(value.trim())) {
      return 'valid_email_message';
    }
    return '';
  };

  const checkEmail = async (value) => {
    if (!value) {
      setIsExisting(null);
      return;
    }
    setCheckingEmail(true);
    try {
      const res = await checkIfEmailExists(appInfo, { user_email: value });
      if (res?.status === 201) {
        setIsExisting(true);
        if (location.pathname !== '/login') {
          navigate('/login', { state: { email: value, activeLogin: true } });
        }
      } else if (res?.status === 200) {
        setIsExisting(false);
        if (location.pathname === '/login') {
          navigate('/register', { state: { email: value } });
        }
      } else {
        setIsExisting(null);
      }
    } catch (err) {
      setIsExisting(false);
    }
    setCheckingEmail(false);
  };

  const onEmailBlur = (e) => {
    const validationKey = getEmailValidationKey(e.target.value);
    setEmailErrorKey(validationKey);
    if (!validationKey) {
      checkEmail(e.target.value.trim());
    }
  };

  useEffect(() => {
    const focusEmailInput = () => {
      const validationKey = getEmailValidationKey(email);
      setEmailErrorKey(validationKey);

      if (emailInputRef.current) {
        emailInputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => {
          emailInputRef.current?.focus({ preventScroll: true });
        }, 100);
      }
    };

    window.addEventListener('kids-register-focus-email', focusEmailInput);
    return () => window.removeEventListener('kids-register-focus-email', focusEmailInput);
  }, [email]);


  const handleAuthSuccess = (userId, token, userDetails, isNewRegistration) => {
    localStorage.setItem('userId', userId);
    if (token) dispatch(getAccessToken({ accessToken: token }));
    if (userDetails) dispatch(getUserDetails({ userDetails }));

    if (isNewRegistration) {
      setPendingUserId(userId);
      setShowSetupModal(true);
    } else {
      dispatch(getUser({ user: userId }));
      navigate('/home');
    }
  };

  const onPrimaryClick = async () => {
    if (!email || !password) {
      toast.error('Please enter email and password');
      return;
    }
    setSubmitting(true);
    try {
      if (location.pathname === '/login' || isExisting) {
        const loginRes = await loginService.login({ email, password }, appInfo);
        if (loginRes?.status === 200 && loginRes?.data?.data?.[0]?.user_id) {
          handleAuthSuccess(
            loginRes.data.data[0].user_id,
            loginRes.data.token,
            loginRes.data
          );
        } else {
          toast.error(loginRes?.data?.message || 'Login failed');
        }
      } else {
        const regValues = { email, password };
        const regRes = await registerService.registerUser(appInfo, regValues);
        if (regRes?.status === 200 && regRes?.data?.data?.[0]?.user_id) {
          handleAuthSuccess(
            regRes.data.data[0].user_id,
            regRes.data.token,
            regRes.data,
            true
          );
        } else {
          toast.error(regRes?.data?.message || 'Registration failed');
        }
      }
    } catch (err) {
      toast.error('Something went wrong');
    }
    setSubmitting(false);
  };

  const googleLoginHandler = useGoogleLogin({
    onSuccess: (codeResponse) => setGoogleUser(codeResponse),
    onError: (error) => { },
  });

  const getgoogleUserData = async () => {
    const response = await loginService.googleLogin(googleUser?.access_token);
    if (response?.status === 200) {
      const values = {
        googleId: response?.data?.id,
        loginType: 'google',
        firstName: response?.data?.name,
        email: response?.data?.email,
      };
      loginWithoutCode(values);
    }
  };

  useEffect(() => {
    if (googleUser) {
      getgoogleUserData();
    }
  }, [googleUser]);

  useEffect(() => {
    if (applePrivateEmail) {
      const appleData = { ...applePrivateData, email: applePrivateEmail };
      appleLoginWithGizmott(appleData);
    }
  }, [applePrivateEmail]);

  useEffect(() => {
    if (projectInfo?.projectConfig?.config?.APPLE_LOGIN_REQUIRED == 'true' && projectInfo?.projectConfig?.config?.APPLE_CLIENT_ID) {
      const redirectURI = projectInfo?.projectConfig?.config?.APPLE_LOGIN_REDIRECT_URL;
      if (!redirectURI || typeof redirectURI !== 'string') return;
      const script = document.createElement('script');
      script.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
      script.async = true;
      document.body.appendChild(script);
      script.onload = () => {
        if (window.AppleID) {
          window.AppleID.auth.init({
            clientId: projectInfo?.projectConfig?.config?.APPLE_CLIENT_ID,
            scope: 'email name',
            redirectURI: redirectURI,
            usePopup: true,
          });
          document.addEventListener('AppleIDSignInOnSuccess', (event) => {
            const appleData = jwtDecode(event?.detail?.authorization?.id_token);
            if (appleData?.sub) {
              if (appleData?.is_private_email == 'true' && appleData?.sub) {
                setApplePrivateData(appleData);
                setApplePrivateLogin(true);
              } else {
                appleLoginWithGizmott(appleData);
              }
            } else {
              toast.error(<Translate textKey={'try_again_msg'} />);
            }
          });
          document.addEventListener('AppleIDSignInOnFailure', (event) => {
            console.error('Apple Sign-in failed:', event.detail.error);
          });
        }
      };
      return () => {
        document.body.removeChild(script);
      };
    }
  }, [projectInfo]);

  const appleLoginWithGizmott = (data) => {
    const { email, sub } = data;
    const name = email?.split('@')[0];
    const appleUserDetails = {
      appleId: sub,
      loginType: 'apple',
      firstName: name,
      email,
    };
    loginWithoutCode(appleUserDetails);
  };

  const loginWithoutCode = async (values) => {
    try {
      const response = await loginService.updateLoginWithoutCode(appInfo, values);
      if (response?.status === 200) {
        const userId = response?.data?.data[0]?.user_id;
        handleAuthSuccess(userId, response?.data?.token, response?.data);
      } else {
        toast.error(response?.data?.message || 'Sign-in failed');
      }
    } catch (err) {
      toast.error('Something went wrong');
    }
  };

  return (
    <div className="kids-landing kids-template">
      <ToastContainer />
      <ParentalPasscodeModal
        isOpen={showSetupModal}
        mode="setup"
        appInfo={appInfo}
        showClose={false}
        onSuccess={() => {
          setShowSetupModal(false);
          if (pendingUserId) {
            dispatch(getUser({ user: pendingUserId }));
            setPendingUserId(null);
          }
          navigate('/home');
        }}
      />
      {applePrivateLogin && (
        <ApplePrivateLoginModal
          isEmailForm={true}
          setApplePrivateLogin={setApplePrivateLogin}
          setApplePrivateEmail={setApplePrivateEmail}
        />
      )}
      <div className="kids-landing__center">
        {projectInfo?.projectConfig?.pubid === 50174 && <><h1 className="kids-landing__title">A safe Harbour for Kids</h1>
        <h2 className="kids-landing__subtitle">Free forever</h2>
        <p className="kids-landing__desc">Ready to watch? Enter your details</p></>}
        <div className="kids-landing__form">
          {projectInfo?.projectConfig?.config?.APPLE_LOGIN_REQUIRED == 'true' && (
            <button className="kids-landing__apple" onClick={() => window.AppleID?.auth?.signIn()}>
              <AppleIcon className="kids-landing__apple-icon" /> <Translate textKey={'apple_sign_in'} />
            </button>
          )}
          <button className="kids-landing__google" onClick={() => googleLoginHandler()}>
            <GoogleIcon className="kids-landing__google-icon" /> <Translate textKey={'google_sign_in'} />
          </button>
          <div className="kids-landing__or">
            <hr />
            <span><Translate textKey={'or'} /></span>
          </div>
          <p className="kids-landing__email-label"><Translate textKey={'sign_in_via_email_password'} /></p>
          <input
            ref={emailInputRef}
            className="kids-landing__input"
            aria-label="email"
            aria-invalid={Boolean(emailErrorKey)}
            placeholder="Email"
            value={email}
            onChange={(e) => {
              const nextEmail = e.target.value;
              setEmail(nextEmail);
              if (emailErrorKey) {
                setEmailErrorKey(getEmailValidationKey(nextEmail));
              }
            }}
            onBlur={onEmailBlur}
          />
          {emailErrorKey && <p className="kids-landing__email-error"><Translate textKey={emailErrorKey} /></p>}
          <input
            className="kids-landing__input"
            aria-label="password"
            placeholder={isLogin ? "Password" : "Create Password"}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <button
            className="kids-landing__cta"
            onClick={onPrimaryClick}
            disabled={checkingEmail || submitting}
          >
            {checkingEmail || submitting
              ? 'Please wait...'
              : (location.pathname === '/login' || isExisting
                  ? <Translate textKey={'sign_in'} />
                  : <Translate textKey={'create_account'} />)}
          </button>
        </div>
      </div>
      {!isLogin && (
        <div className="kids-landing__trending">
          {loading && <div>Loading...</div>}
          {!loading && (
            <>
              {topTrending && <TopTrendingEpisodes data={topTrending} isRegisterPage={true} />}
            </>
          )}
        </div>
      )}
    </div>
  );
};

const EmailForm = ({ onCheck }) => {
  const [email, setEmail] = useState('');
  const [dirty, setDirty] = useState(false);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'center' }}>
      <input
        aria-label="email"
        placeholder="Email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        onBlur={() => setDirty(true)}
        style={{ padding: '12px 16px', width: '100%', maxWidth: 420, borderRadius: 24, border: '1px solid #ddd' }}
      />
      <div style={{ width: '100%', maxWidth: 420 }}>
        <button
          onClick={() => onCheck(email)}
          style={{ width: '100%', padding: '12px 16px', borderRadius: 24, background: 'var(--button-color)', color: 'var(--button-text-color)', border: 'none' }}
        >
          Continue
        </button>
      </div>
    </div>
  );
};

export default KidsLanding;
